import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { BarChart2, Users, Upload, Clock, LogIn, Layers } from 'lucide-react';

const API = process.env.REACT_APP_API_URL || 'https://api.orbit-technology.com/api';
const HOUR_LABELS = ['12a','1a','2a','3a','4a','5a','6a','7a','8a','9a','10a','11a','12p','1p','2p','3p','4p','5p','6p','7p','8p','9p','10p','11p'];

function StatCard({ icon: Icon, label, value, sub, color = 'text-blue-600', bg = 'bg-blue-50', wide = false }) {
  return (
    <div className={`bg-white border border-gray-100 rounded-2xl shadow-sm p-5 ${wide ? 'md:col-span-2' : ''}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-9 h-9 ${bg} rounded-xl flex items-center justify-center`}>
          <Icon size={16} className={color} />
        </div>
        <span className="text-gray-400 text-xs font-medium uppercase tracking-wide">{label}</span>
      </div>
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
      {sub && <p className="text-gray-400 text-xs mt-1">{sub}</p>}
    </div>
  );
}

// One place for these, so the two breakdowns below cannot label the same
// category differently.
const CAT_LABELS = {
  bank_statements: 'Bank Statements',
  application: 'Application',
  drivers_license: 'Driver\'s Licence',
  voided_check: 'Voided Check',
  credit_report: 'Credit Report',
  other: 'Other',
};

function formatDuration(sec) {
  if (!sec || sec < 1) return '—';
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ${sec % 60}s`;
  return `${Math.floor(sec / 3600)}h ${Math.floor((sec % 3600) / 60)}m`;
}

export default function ClientData() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetch(`${API}/client-data/metrics`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : null),
      fetch(`${API}/documents/client/all`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : []),
    ]).then(([metrics, allDocs]) => { setData(metrics); setDocs(Array.isArray(allDocs) ? allDocs : allDocs?.data || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return <div className="p-6 text-gray-400 text-sm">Loading metrics...</div>;
  if (!data) return <div className="p-6 text-gray-400 text-sm">Unable to load client data.</div>;

  const { summary, uploadsByCategory, clientDetails, uploaderCountsByCategory: uploaderCounts } = data;

  // Multi-type: uploaded 2+ different doc types (excl application) — inclusive of single
  const multiTypeUploaders = clientDetails.filter(c => {
    const nonAppCats = (c.categories || []).filter(cat => cat !== 'application');
    return nonAppCats.length >= 2;
  }).length;

  // Daily averages
  const dayCount = Object.keys(data.uploadsByDay || {}).length || 1;
  const dailyAvgClients = (summary.totalClients / dayCount).toFixed(1);
  const dailyAvgLogins = (summary.uniqueLoggedIn / dayCount).toFixed(1);
  const dailyAvgUploads = (summary.totalUploads / dayCount).toFixed(1);

  // Upload time-of-day by category
  const catLabels = { bank_statements: 'Bank Statements', application: 'Application', drivers_license: 'ID', voided_check: 'Voided Check', other: 'Other' };
  const catColors = { bank_statements: '#3b82f6', application: '#8b5cf6', drivers_license: '#f59e0b', voided_check: '#10b981', other: '#6b7280' };
  const hourData = {};
  docs.forEach(d => {
    if (!d.uploadedAt) return;
    const h = new Date(d.uploadedAt).getHours();
    const cat = d.category || 'other';
    if (!hourData[cat]) hourData[cat] = new Array(24).fill(0);
    hourData[cat][h]++;
  });
  const maxHourVal = Math.max(1, ...Object.values(hourData).flatMap(a => a));

  return (
    <div className="p-6 space-y-5 max-w-6xl mx-auto">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 tracking-tight flex items-center gap-2.5">
          <BarChart2 size={22} className="text-blue-600" /> Client Data
        </h1>
        <p className="text-gray-400 text-sm mt-0.5">Engagement metrics and upload behavior</p>
        {/* Says plainly what these figures count. Every upload number here is
            the client's own: files a rep or admin put on a client's record, and
            files DocuSign or a webhook filed automatically, are not client
            activity and are left out - which is most of the document index. */}
        {summary.excludedUploads && (
          <p className="text-gray-400 text-xs mt-1">
            Client activity only — {(
              (summary.excludedUploads.staff || 0) +
              (summary.excludedUploads.automated || 0) +
              (summary.excludedUploads.unknown || 0)
            ).toLocaleString()} files on client records were not uploaded by the client and are
            left out: {(summary.excludedUploads.staff || 0).toLocaleString()} by reps or admins,
            {' '}{(summary.excludedUploads.automated || 0).toLocaleString()} from DocuSign or a
            webhook, {(summary.excludedUploads.unknown || 0).toLocaleString()} from a name that
            matches no account. A rep adding statements to work a deal is not a client upload.
          </p>
        )}
      </div>

      {/* Row 1: 3 boxes */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard icon={Users} label="Total Clients" value={summary.totalClients}
          sub="All registered clients" />
        <StatCard icon={LogIn} label="Clients Logged In" value={summary.uniqueLoggedIn}
          sub={`${summary.totalClients > 0 ? ((summary.uniqueLoggedIn / summary.totalClients) * 100).toFixed(1) : 0}% login rate`}
          color="text-green-600" bg="bg-green-50" />
        {/* THE QUESTION THIS PAGE IS FOR: how many merchants sent us something
            themselves. Excludes the signed application, which arrives from
            DocuSign on every deal and says nothing about whether the client
            did anything, and excludes every file a rep or admin put on a
            client's record. Counted server-side so it is the same number
            wherever it is read. */}
        <StatCard icon={Upload} label="Clients Who Sent Documents"
          value={summary.clientsWhoSuppliedDocs ?? '—'}
          sub={`${summary.suppliedRate ?? 0}% of all clients · excludes the application`}
          color="text-indigo-600" bg="bg-indigo-50" />
      </div>

      {/* Row 2: 4 boxes */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard icon={Layers} label="Multi-Type Uploaders" value={multiTypeUploaders}
          sub={`Uploaded 2+ doc types`}
          color="text-purple-600" bg="bg-purple-50" />
        <StatCard icon={Clock} label="Avg Session Duration" value={formatDuration(summary.avgSessionSec)}
          sub={summary.maxSessionSec > 0 ? `Longest: ${formatDuration(summary.maxSessionSec)}` : 'No session data yet'}
          color="text-amber-600" bg="bg-amber-50" />
        <StatCard icon={Clock} label="Avg Login Time" value={summary.avgLoginHour !== null ? HOUR_LABELS[summary.avgLoginHour] : '—'}
          sub="Average time-of-day clients log in"
          color="text-teal-600" bg="bg-teal-50" />
        <StatCard icon={LogIn} label="Avg First Login" value={summary.avgFirstLoginHours !== null ? `${summary.avgFirstLoginHours}h` : '—'}
          sub="Hours from account creation to first login"
          color="text-rose-600" bg="bg-rose-50" />
      </div>

      {/* HOW MANY CLIENTS sent each type, which is a different question from
          how many files arrived - one client sending six months of statements
          is six files and one client. */}
      {uploaderCounts && Object.keys(uploaderCounts).length > 0 && (
        <div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-5">
          <h2 className="text-gray-900 font-semibold text-sm mb-1 flex items-center gap-2">
            <Users size={15} className="text-indigo-600" /> Clients who sent each document type
          </h2>
          <p className="text-gray-400 text-xs mb-4">
            Distinct clients, not file counts. The signed application is excluded — it comes from
            DocuSign, not from the client.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(uploaderCounts).map(([cat, n]) => (
              <div key={cat} className="bg-indigo-50/50 border border-indigo-100 rounded-xl p-3">
                <p className="text-xl font-bold text-indigo-700">{n}</p>
                <p className="text-gray-500 text-xs mt-0.5">{CAT_LABELS[cat] || cat}</p>
                <p className="text-gray-400 text-[10px] mt-0.5">
                  {summary.totalClients > 0 ? ((n / summary.totalClients) * 100).toFixed(1) : 0}% of clients
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Category Breakdown */}
      <div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-5">
        <h2 className="text-gray-900 font-semibold text-sm mb-4 flex items-center gap-2">
          <BarChart2 size={15} className="text-blue-600" /> Uploads by Category
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries(uploadsByCategory).sort((a, b) => b[1] - a[1]).map(([cat, count]) => {
            const labels = { bank_statements: 'Bank Statements', application: 'Application', drivers_license: 'ID', voided_check: 'Voided Check', other: 'Other' };
            return (
              <div key={cat} className="bg-gray-50 rounded-xl p-3">
                <p className="text-gray-500 text-xs">{labels[cat] || cat}</p>
                <p className="text-gray-900 font-bold text-lg">{count}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Upload Time-of-Day Chart */}
      <div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-5">
        <h2 className="text-gray-900 font-semibold text-sm mb-2 flex items-center gap-2">
          <Clock size={15} className="text-amber-600" /> Average Upload Time by Document Type
        </h2>
        <p className="text-gray-400 text-xs mb-4">When clients upload each type of document throughout the day</p>
        {Object.keys(hourData).length === 0 ? (
          <p className="text-gray-300 text-sm text-center py-8">No upload data yet</p>
        ) : (
          <div>
            <div className="flex flex-wrap gap-3 mb-4">
              {Object.keys(hourData).map(cat => (
                <div key={cat} className="flex items-center gap-1.5">
                  <div style={{ width: 10, height: 10, borderRadius: 3, background: catColors[cat] || '#6b7280' }} />
                  <span className="text-xs text-gray-500">{catLabels[cat] || cat}</span>
                </div>
              ))}
            </div>
            <div style={{ overflowX: 'auto' }}>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, minWidth: 600, height: 160 }}>
                {HOUR_LABELS.map((label, h) => {
                  const cats = Object.keys(hourData);
                  return (
                    <div key={h} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <div style={{ display: 'flex', flexDirection: 'column-reverse', width: '100%', height: 130 }}>
                        {cats.map(cat => {
                          const val = hourData[cat][h] || 0;
                          const pct = (val / maxHourVal) * 100;
                          return pct > 0 ? (
                            <div key={cat} title={`${catLabels[cat] || cat}: ${val}`}
                              style={{ width: '100%', height: `${pct}%`, background: catColors[cat] || '#6b7280', borderRadius: '3px 3px 0 0', minHeight: 3 }} />
                          ) : null;
                        })}
                      </div>
                      <span style={{ fontSize: 9, color: '#9ca3af', marginTop: 4 }}>{label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
